import React from 'react';

interface PendingCheckoutNoticeProps {
  /** Name of the plan the unfinished checkout was for. */
  planName: string;
  /** Name of the plan the workspace is actually on. */
  currentPlanName: string;
  /** Only billing managers can pay; everyone else is told who can. */
  canManageBilling: boolean;
  onComplete: () => void;
}

/**
 * Shown when a checkout was opened but Revolut has no payment for it.
 *
 * The unpaid checkout grants nothing, so the copy says plainly that the plan
 * did not change and offers the one way forward. It claims only what is known
 * — the payment hasn't come through — never "nothing was charged": the billing
 * read asks Revolut first, but if Revolut can't be reached a just-made payment
 * may simply not be visible yet. Before this existed the plan showed as
 * "Pending" with no way to pay.
 */
export default function PendingCheckoutNotice({
  planName,
  currentPlanName,
  canManageBilling,
  onComplete,
}: PendingCheckoutNoticeProps) {
  return (
    <div
      data-testid="pending-checkout-notice"
      className="mb-6 flex flex-col gap-3 rounded-lg border border-warning-200 bg-warning-50 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-warning-800 dark:bg-warning-900/20"
    >
      <div className="text-sm text-warning-800 dark:text-warning-200">
        <p className="font-semibold">You haven&apos;t finished upgrading to {planName}</p>
        <p className="mt-1">
          Payment for this checkout hasn&apos;t come through, so this workspace is still on{' '}
          {currentPlanName}.{!canManageBilling && ' Ask an Owner or Admin to complete it.'}
        </p>
      </div>
      {canManageBilling && (
        <button
          type="button"
          onClick={onComplete}
          className="shrink-0 self-start px-6 py-2 bg-primary-500 dark:bg-primary-350 rounded-full text-sm font-medium hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors sm:self-auto"
        >
          Complete payment
        </button>
      )}
    </div>
  );
}
